import { getCouponBenefitSummary, getCouponTargetText } from "@/utils/couponCheckout.utils";
import { formatCouponDateTime } from "@/utils/coupon.utils";

// 母券 v2 公開欄位整理；與舊 storeCoupon.model（coupons_id）隔離
export function getPublicTemplateView(data) {
  if (
    !data ||
    typeof data.template_uuid !== "string" ||
    !data.template_uuid ||
    typeof data.offer_uuid !== "string" ||
    !data.offer_uuid
  ) {
    throw new Error("活動資料不完整，請重新整理");
  }
  return {
    template_uuid: data.template_uuid,
    offer_uuid: data.offer_uuid,
    title: data.title || "優惠活動",
    description: data.description ?? "",
    offer_channel: data.offer_channel,
    claim_begin_at: data.claim_begin_at,
    claim_end_at: data.claim_end_at,
    timezone_name: data.timezone_name,
    public_rules: data.public_rules ?? null,
    image: data.image ?? null,
  };
}

export function getCouponImage(image) {
  if (!image || typeof image !== "object" || Array.isArray(image)) return null;
  const getImageUrl = (url) => typeof url === "string" && url.startsWith("https://") && url.length > 8
    ? url : null;
  const imageUrl = getImageUrl(image.image_url);
  const previewUrl = getImageUrl(image.preview_image_url);
  if (!imageUrl && !previewUrl) return null;
  return {
    imageUrl: imageUrl || previewUrl,
    previewUrl: previewUrl || imageUrl,
    width: Number.isInteger(image.width) && image.width > 0 ? image.width : null,
    height: Number.isInteger(image.height) && image.height > 0 ? image.height : null,
  };
}

export function getPublicTemplatePage(data) {
  if (
    !Array.isArray(data?.items) ||
    !Number.isInteger(data.page) ||
    data.page < 1 ||
    !Number.isInteger(data.page_size) ||
    data.page_size < 1 ||
    !Number.isInteger(data.total) ||
    data.total < 0
  ) {
    throw new Error("活動清單資料不完整，請重新整理");
  }
  return {
    items: data.items.map(getPublicTemplateView),
    page: data.page,
    page_size: data.page_size,
    total: data.total,
  };
}

// 與 POS 的效期規則一致；領取後的實際起訖仍由 API effective_* 提供。
export function getTemplateValidityText(publicRules, timezoneName = "Asia/Taipei") {
  const unitText = { hour: "小時", day: "天", week: "週", month: "個月" };
  if (
    publicRules?.validity_mode === "after_claim" &&
    Number.isInteger(publicRules.duration_value) &&
    publicRules.duration_value > 0 &&
    unitText[publicRules.duration_unit]
  ) {
    return `領取後 ${publicRules.duration_value} ${
      unitText[publicRules.duration_unit]
    }內有效`;
  }
  if (publicRules?.validity_mode === "permanent") {
    return "優惠活動期間內有效";
  }
  if (publicRules?.validity_mode === "calendar_cycle") {
    const cycleText = {
      seven_days: "七天有效",
      claim_month: "當月有效",
      claim_year: "當年有效",
      birthday_month: "生日當月有效",
      campaign: "活動期間有效（舊資料）",
    };
    return cycleText[publicRules.validity_cycle] || "";
  }
  if (publicRules?.validity_mode === "fixed_range" &&
      Number.isFinite(Date.parse(publicRules.fixed_valid_until_at))) {
    return `有效至 ${formatCouponDateTime(publicRules.fixed_valid_until_at, timezoneName)}`;
  }
  return "";
}

// 公開詳情僅展示規則；不套用線上點餐驗券的版本或購物車限制。
export function getCouponPublicRuleDetail({ publicRules }) {
  const rules = publicRules || {};
  const isOrder = rules.benefit_scope === "order";
  const isTarget = rules.benefit_scope === "target";
  const isExchange = rules.benefit_type === "target_exchange";
  const hasQuantity = Number.isInteger(rules.benefit_quantity) && rules.benefit_quantity > 0;
  const hasBenefit = rules.benefit_type === "percentage"
    ? Number.isFinite(rules.pay_rate_percent) && rules.pay_rate_percent > 0 && rules.pay_rate_percent <= 100
    : rules.benefit_type === "fixed_amount"
      ? Number.isFinite(isOrder ? rules.order_discount_amount : rules.target_unit_discount_amount)
        && (isOrder ? rules.order_discount_amount : rules.target_unit_discount_amount) > 0
      : isExchange;
  const benefitTexts = [hasBenefit && (isOrder && !isExchange || isTarget && hasQuantity)
    ? getCouponBenefitSummary(rules) : "優惠內容依優惠券設定"];
  const getTargetText = (target) => target && ["product", "combo", "spec_option"].includes(target.target_type)
    ? getCouponTargetText({ target }) : "指定商品或規格";
  if (isTarget && Array.isArray(rules.benefit_targets) && rules.benefit_targets.length) {
    benefitTexts.push(`適用：${rules.benefit_targets.map(getTargetText).join("、")}`);
  }
  const hasMinAmount = Number.isFinite(rules.min_order_amount) && rules.min_order_amount >= 0;
  const requiredTargets = Array.isArray(rules.required_targets) ? rules.required_targets : [];
  // 無金額門檻時只列商品條件；連商品條件都沒有才顯示無門檻限制
  const thresholdTexts = !hasMinAmount ? ["使用門檻依優惠券設定"]
    : rules.min_order_amount > 0 ? [`消費滿 $${rules.min_order_amount} 可使用`]
      : requiredTargets.length ? [] : ["無門檻限制"];
  if (hasMinAmount && rules.min_order_amount > 0 && requiredTargets.length) {
    const conditions = requiredTargets.map((target) => {
      const text = getTargetText(target);
      const targetTypeText = { product: "商品", combo: "套餐", spec_option: "規格" }[target?.target_type];
      const quantityText = Number.isInteger(target?.required_quantity) && target.required_quantity > 0
        ? ` x ${target.required_quantity}` : "";
      return `${targetTypeText && !text.startsWith("指定") ? `${targetTypeText} ` : ""}${text}${quantityText}`;
    });
    const conditionText = requiredTargets.length > 1 && rules.required_target_match_mode === "any"
      ? `(${conditions.join(" 或 ")})` : conditions.join(", 且 ");
    return { benefitTexts, thresholdTexts: [`最低消費 ${rules.min_order_amount} 元, 且 ${conditionText}`] };
  }
  if (requiredTargets.length > 1) {
    thresholdTexts.push(rules.required_target_match_mode === "any" ? "以下指定條件符合其中一項即可。" : "以下指定條件須全部符合。");
  }
  for (const target of requiredTargets) {
    const quantityText = Number.isInteger(target?.required_quantity) && target.required_quantity > 0
      ? `，至少 ${target.required_quantity} 份` : "";
    thresholdTexts.push(`${target?.target_type === "spec_option" ? "需符合" : "需購買"}${getTargetText(target)}${quantityText}`);
  }
  return { benefitTexts, thresholdTexts };
}
